/* eslint-disable no-console */
/* eslint-disable no-restricted-syntax */
import { IS_APP_DEVELOPMENT, IS_APP_PRODUCTION, } from '@/configs/app.config';

/* eslint-disable @typescript-eslint/no-explicit-any */
type LogLevel = 'debug' | 'info' | 'warn' | 'error';

interface LogOptions {
  level?: LogLevel;
  group?: string;
  timestamp?: boolean;
  showInProduction?: boolean;
}

class Logger {
  private isProduction: boolean;
  private isDevelopment: boolean;

  constructor() {
    this.isProduction = IS_APP_PRODUCTION || import.meta.env.MODE === 'production';
    this.isDevelopment = IS_APP_DEVELOPMENT || import.meta.env.MODE === 'development';
  }

  private shouldLog(options: LogOptions = {}): boolean {
    if (options.showInProduction) return true;
    return !this.isProduction;
  }

  private formatMessage(level: LogLevel, message: string, options: LogOptions = {}): string {
    const timestamp = options.timestamp !== false ? `[${new Date().toISOString()}]` : '';
    const group = options.group ? `[${options.group}]` : '';
    const levelTag = `[${level.toUpperCase()}]`;

    return `${timestamp}${group}${levelTag} ${message}`.trim();
  }

  private logInternal(level: LogLevel, message: string, data?: any, options: LogOptions = {}) {
    if (!this.shouldLog(options)) return;

    const formattedMessage = this.formatMessage(level, message, options);

    switch (level) {
      case 'debug':
        console.debug(formattedMessage, data);
        break;
      case 'info':
        console.info(formattedMessage, data);
        break;
      case 'warn':
        console.warn(formattedMessage, data);
        break;
      case 'error':
        console.error(formattedMessage, data);
        break;
      default:
        console.log(formattedMessage, data);
    }
  }

  debug(message: string, data?: any, options?: LogOptions) {
    this.logInternal('debug', message, data, options);
  }

  info(message: string, data?: any, options?: LogOptions) {
    this.logInternal('info', message, data, options);
  }

  warn(message: string, data?: any, options?: LogOptions) {
    this.logInternal('warn', message, data, options);
  }

  error(message: string, error?: any, options?: LogOptions) {
    this.logInternal('error', message, error, options);
  }

  group(label: string, callback: () => void, options?: LogOptions) {
    if (!this.shouldLog(options)) {
      callback();
      return;
    }

    console.group(label);
    try {
      callback();
    } finally {
      console.groupEnd();
    }
  }

  table(data: any, options?: LogOptions) {
    if (!this.shouldLog(options)) return;
    console.table(data);
  }

  time(label: string, options?: LogOptions) {
    if (!this.shouldLog(options)) return;
    console.time(label);
  }

  timeEnd(label: string, options?: LogOptions) {
    if (!this.shouldLog(options)) return;
    console.timeEnd(label);
  }

  // Backward compatibility
  log(...args: any[]) {
    if (!this.shouldLog()) return;
    console.log(...args);
  }
}

// Create singleton instance
const loggerInstance = new Logger();

// Export the logger instance
export const LOGGER = loggerInstance;

// Export individual methods for convenience
export const debug = loggerInstance.debug.bind(loggerInstance);
export const info = loggerInstance.info.bind(loggerInstance);
export const warn = loggerInstance.warn.bind(loggerInstance);
export const error = loggerInstance.error.bind(loggerInstance);
export const group = loggerInstance.group.bind(loggerInstance);
export const table = loggerInstance.table.bind(loggerInstance);
export const time = loggerInstance.time.bind(loggerInstance);
export const timeEnd = loggerInstance.timeEnd.bind(loggerInstance);
export const log = loggerInstance.log.bind(loggerInstance);

// Export types for external use
export type { LogLevel, LogOptions };

/*
USAGE EXAMPLES:

1. Basic Logging (returns void):
LOGGER('Hello world'); // Returns: undefined
info('User action'); // Returns: undefined
warn('Warning message'); // Returns: undefined
error('Error occurred'); // Returns: undefined

2. Logging with Data (returns void):
info('User data', { id: 1, name: 'John' }); // Returns: undefined
debug('API response', { status: 200, data: [...] }); // Returns: undefined

3. Logging with Options (returns void):
info('Message', null, { group: 'Auth', timestamp: true }); // Returns: undefined
error('Error', error, { showInProduction: true }); // Returns: undefined

4. Group Logging (returns void):
group('User Actions', () => {
  info('Button clicked');
  info('Form submitted');
}); // Returns: undefined

5. Performance Timing (returns void):
time('api-call'); // Returns: undefined
// ... API call
timeEnd('api-call'); // Returns: undefined

6. Table Logging (returns void):
table([
  { id: 1, name: 'John', age: 30 },
  { id: 2, name: 'Jane', age: 25 }
]); // Returns: undefined

7. Conditional Logging (returns void):
const shouldLog = true;
if (shouldLog) {
  info('Conditional message'); // Returns: undefined
}

8. Error Handling (returns void):
try {
  // Some operation
} catch (err) {
  error('Operation failed', err); // Returns: undefined
}

9. Production vs Development:
// In development: logs to console
// In production: returns early (unless showInProduction: true)

10. Chaining (not recommended, all methods return void):
LOGGER('First').log('Second'); // This won't work as expected

11. Return Values in Functions:
function processUser(userId: number) {
  info('Processing user', { userId }); // Returns: undefined
  
  if (!userId) {
    error('Invalid user ID'); // Returns: undefined
    return null; // Function returns null
  }
  
  // Process user...
  info('User processed successfully'); // Returns: undefined
  return { success: true }; // Function returns object
}

12. Async Functions:
async function fetchData() {
  time('fetch-data'); // Returns: undefined
  
  try {
    const response = await api.get('/data');
    info('Data fetched', { status: response.status }); // Returns: undefined
    return response.data; // Function returns data
  } catch (err) {
    error('Failed to fetch data', err); // Returns: undefined
    throw err; // Function throws error
  } finally {
    timeEnd('fetch-data'); // Returns: undefined
  }
}

13. React Component Example:
function UserProfile({ userId }: { userId: number }) {
  useEffect(() => {
    info('User profile loaded', { userId }); // Returns: undefined
    
    return () => {
      info('User profile unmounted', { userId }); // Returns: undefined
    };
  }, [userId]);
  
  return <div>Profile for user {userId}</div>;
}

14. Custom Logger Instance:
const customLogger = new Logger();
customLogger.info('Custom message'); // Returns: undefined

15. TypeScript with Return Types:
function validateInput(input: string): boolean {
  if (!input) {
    error('Empty input provided'); // Returns: undefined
    return false; // Function returns boolean
  }
  
  info('Input validated successfully'); // Returns: undefined
  return true; // Function returns boolean
}

16. Error Handling Examples:

// Standard Error
try {
  throw new Error('Something went wrong');
} catch (err) {
  error('Standard error caught', err);
  // Logs: { name: 'Error', message: 'Something went wrong', stack: '...' }
}

// Axios Error
try {
  await axios.get('/api/users');
} catch (err) {
  error('API request failed', err);
  // Logs: { 
  //   message: 'Request failed with status code 404',
  //   status: 404,
  //   response: { data: {...}, status: 404, statusText: 'Not Found' },
  //   config: {...},
  //   request: {...}
  // }
}

// API Error Object
const apiError = {
  message: 'Validation failed',
  status: 400,
  errors: [
    { field: 'email', message: 'Invalid email format' },
    { field: 'password', message: 'Password too short' }
  ]
};
error('API validation error', apiError);
// Logs: { message: 'Validation failed', status: 400, errors: [...] }

// Custom Error Object
const customError = {
  code: 'AUTH_FAILED',
  message: 'Authentication failed',
  details: { userId: 123, reason: 'Token expired' }
};
error('Authentication error', customError);
// Logs: { message: 'Authentication failed', code: 'AUTH_FAILED', details: {...} }

// Primitive Error
error('Simple error', 'Network timeout');
// Logs: 'Network timeout'

// Undefined Error
error('No error details');
// Logs: undefined
*/
